<template>
  <div>
    <main-navigation></main-navigation>

    <div class="sms-container">

      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center mb-4 header-row">
        <h2 class="page-title">Send SMS to Tenants</h2>
      </div>

      <!-- MAIN CARD -->
      <div class="row">
        <!-- LEFT COLUMN - Tenant Selection -->
        <div class="col-md-5">
          <div class="card shadow-sm border-0 p-3 selection-card">
            <div class="card-header bg-transparent border-0 pt-2 pb-3">
              <h5 class="card-title mb-0">
                <i class="bi bi-people-fill me-2"></i>
                Select Tenants
              </h5>
              <p class="text-muted small mt-1">Choose tenants to receive your message</p>
            </div>

            <!-- House Selection Buttons -->
            <div class="house-selection mb-3">
              <div class="d-flex gap-2 mb-2">
                <button 
                  @click="selectAllTenants" 
                  class="btn btn-sm btn-outline-primary flex-grow-1"
                  :class="{ 'btn-primary text-white': selectedFilter === 'all' }"
                >
                  <i class="bi bi-building"></i> All Houses
                </button>
                <button 
                  @click="showHouseSelector = !showHouseSelector" 
                  class="btn btn-sm btn-outline-secondary"
                >
                  <i class="bi bi-grid-3x3-gap-fill"></i> Select by House
                </button>
              </div>
              
              <!-- House Selector Dropdown -->
              <div v-if="showHouseSelector" class="house-selector">
                <div class="house-list">
                  <button 
                    v-for="house in houses" 
                    :key="house.id"
                    @click="selectByHouse(house.id)"
                    class="house-btn"
                    :class="{ active: selectedHouseId === house.id }"
                  >
                    <i class="bi bi-building"></i>
                    {{ house.name || house.house_name }}
                    <span class="badge bg-secondary ms-1">{{ getTenantCountForHouse(house.id) }}</span>
                    <span class="badge bg-info ms-1">{{ getUnitCountForHouse(house.id) }} units</span>
                  </button>
                </div>
                <div class="mt-2" v-if="selectedHouseId">
                  <button @click="clearHouseSelection" class="btn btn-sm btn-link">
                    <i class="bi bi-x-circle"></i> Clear House Selection
                  </button>
                </div>
              </div>
            </div>

            <!-- Search & Filter -->
            <div class="search-wrapper mb-3">
              <i class="bi bi-search search-icon"></i>
              <input
                v-model="search"
                type="text"
                class="form-control search-box"
                placeholder="Search tenant by name, phone or ID..."
              />
            </div>

            <!-- Selection Summary Bar -->
            <div class="selection-summary-bar mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <span class="badge bg-primary">{{ selectedTenants.length }} selected</span>
                  <span class="badge bg-secondary ms-1">{{ filteredTenants.length }} total</span>
                </div>
                <button 
                  v-if="selectedTenants.length > 0"
                  @click="clearSelection" 
                  class="btn btn-sm btn-link text-danger"
                >
                  <i class="bi bi-trash"></i> Clear
                </button>
              </div>
            </div>

            <!-- Tenant List -->
            <div class="tenant-list">
              <div 
                v-for="tenant in filteredTenants" 
                :key="tenant.id"
                class="tenant-item"
                :class="{ selected: selectedTenants.includes(tenant.id) }"
              >
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="'tenant-' + tenant.id"
                    :value="tenant.id"
                    v-model="selectedTenants"
                  >
                  <label class="form-check-label" :for="'tenant-' + tenant.id">
                    <div class="tenant-info">
                      <div class="tenant-name">
                        <i class="bi bi-person-circle me-1"></i>
                        {{ tenant.tenant_name }}
                        <span class="badge bg-info ms-2" v-if="tenant.house_name">
                          <i class="bi bi-building"></i> {{ tenant.house_name }}
                        </span>
                        <span class="badge bg-secondary ms-2" v-if="tenant.unit_number">
                          <i class="bi bi-door-closed"></i> Unit {{ tenant.unit_number }}
                        </span>
                      </div>
                      <div class="tenant-details">
                        <span class="badge bg-light text-dark me-2">
                          <i class="bi bi-telephone"></i> {{ tenant.tenant_phone || 'No phone' }}
                        </span>
                        <span class="badge bg-light text-dark">
                          <i class="bi bi-card-text"></i> {{ tenant.tenant_idnumber }}
                        </span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <!-- No Results -->
              <div v-if="filteredTenants.length === 0 && !loading" class="text-center text-muted py-5">
                <i class="bi bi-inbox display-4 d-block mb-3"></i>
                <p class="mb-0">No tenants found</p>
              </div>
              
              <!-- Loading -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN - Message Composition -->
        <div class="col-md-7">
          <div class="card shadow-sm border-0 p-3 message-card">
            <div class="card-header bg-transparent border-0 pt-2 pb-3">
              <h5 class="card-title mb-0">
                <i class="bi bi-chat-dots-fill me-2"></i>
                Compose Message
              </h5>
              <p class="text-muted small mt-1">Write your SMS message below</p>
            </div>

            <!-- Message Input -->
            <div class="message-input-wrapper">
              <textarea
                v-model="messageText"
                class="form-control message-input"
                rows="6"
                placeholder="Type your message here..."
                maxlength="160"
                :class="{ 'border-danger': messageText.length > 160 }"
              ></textarea>
              
              <!-- Character Counter -->
              <div class="character-counter" :class="{ 'text-danger': messageText.length > 160, 'text-warning': messageText.length > 140 }">
                <i class="bi bi-text-paragraph"></i>
                {{ messageText.length }} / 160 characters
                <span v-if="messageText.length > 160" class="ms-2">
                  <i class="bi bi-exclamation-triangle-fill"></i> Exceeds limit!
                </span>
              </div>
            </div>

            <!-- Business Name Info -->
            <div class="business-info mt-2" v-if="businessName">
              <small class="text-muted">
                <i class="bi bi-building"></i> Signature will be added: 
                <strong>"{{ businessSignature }}"</strong>
              </small>
            </div>

            <!-- Message Preview with Signature -->
            <div class="message-preview mt-3" v-if="messageText">
              <div class="preview-header">
                <i class="bi bi-eye"></i>
                Preview (including signature)
              </div>
              <div class="preview-content">
                {{ fullMessageWithSignature }}
              </div>
              <div class="character-counter-warning mt-2" v-if="fullMessageWithSignature.length > 160">
                <small class="text-danger">
                  <i class="bi bi-exclamation-triangle-fill"></i> 
                  Full message with signature exceeds 160 characters ({{ fullMessageWithSignature.length }}/160)
                </small>
              </div>
            </div>

            <!-- Quick Templates -->
            <div class="quick-templates mt-3">
              <label class="form-label small fw-semibold">
                <i class="bi bi-lightbulb"></i> Quick Templates
              </label>
              <div class="template-buttons">
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-secondary"
                  @click="applyTemplate('rent')"
                >
                  Rent Reminder
                </button>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-secondary"
                  @click="applyTemplate('maintenance')"
                >
                  Maintenance
                </button>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-secondary"
                  @click="applyTemplate('general')"
                >
                  General Notice
                </button>
              </div>
            </div>

            <!-- Send Button -->
            <div class="send-actions mt-4">
              <button 
                class="btn btn-primary btn-lg w-100"
                @click="sendSMS"
                :disabled="!canSend || sending"
              >
                <span v-if="sending">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  Sending...
                </span>
                <span v-else>
                  <i class="bi bi-envelope-paper-fill me-2"></i>
                  Send SMS to {{ selectedTenants.length }} tenant(s)
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast Message -->
      <div v-if="toastMessage" class="toast-message" :class="toastType">
        <div class="d-flex align-items-center">
          <i :class="toastType === 'toast-success' ? 'bi bi-check-circle-fill' : 'bi bi-exclamation-circle-fill'" class="me-2"></i>
          <div>{{ toastMessage }}</div>
          <button type="button" class="btn-close ms-auto" @click="toastMessage = ''"></button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  name: "SendSMS",
  
  data() {
    return {
      houses: [],
      allUnits: [],
      allTenants: [],
      search: "",
      loading: false,
      sending: false,
      selectedTenants: [],
      messageText: "",
      toastMessage: "",
      toastType: "",
      businessName: "",
      businessSignature: "",
      showHouseSelector: false,
      selectedHouseId: null,
      selectedFilter: "all"
    };
  },

  computed: {
    // Get tenants from units using tenant_id directly
    tenantsFromUnits() {
      console.log("\n=== GETTING TENANTS FROM UNITS ===");
      
      const tenants = [];
      
      // Create a map of tenant_id to tenant for quick lookup
      const tenantMap = new Map();
      this.allTenants.forEach(tenant => {
        tenantMap.set(tenant.id, tenant);
        console.log(`Tenant in map: ID ${tenant.id} -> ${tenant.tenant_name}`);
      });
      
      console.log(`\nTotal tenants in map: ${tenantMap.size}`);
      console.log(`Total units to process: ${this.allUnits.length}`);
      
      // For each unit, if it has a tenant_id, get the tenant details
      this.allUnits.forEach(unit => {
        console.log(`\nProcessing Unit ${unit.unit_number} (ID: ${unit.id})`);
        console.log(`  Unit tenant_id: ${unit.tenant_id}`);
        
        if (unit.tenant_id) {
          const tenant = tenantMap.get(unit.tenant_id);
          
          if (tenant) {
            console.log(`  Found tenant: ${tenant.tenant_name}`);
            
            // Find the house for this unit
            const house = this.houses.find(h => h.id === unit.house_id);
            
            tenants.push({
              id: tenant.id,
              tenant_name: tenant.tenant_name,
              tenant_phone: tenant.tenant_phone,
              tenant_idnumber: tenant.tenant_idnumber,
              unit_id: unit.id,
              unit_number: unit.unit_number,
              house_id: unit.house_id,
              house_name: house ? (house.name || house.house_name) : null,
              rent_amount: unit.rent_amount
            });
            
            console.log(`  ✓ Added tenant ${tenant.tenant_name} to house ${house?.name || 'Unknown'}`);
          } else {
            console.log(`  ✗ No tenant found with ID ${unit.tenant_id}`);
          }
        } else {
          console.log(`  Unit has no tenant (vacant)`);
        }
      });
      
      console.log(`\n✓ Total tenants found in units: ${tenants.length}`);
      return tenants;
    },

    // Filter tenants based on search and house selection
    filteredTenants() {
      let filtered = this.tenantsFromUnits;
      
      // Filter by selected house
      if (this.selectedHouseId) {
        filtered = filtered.filter(t => t.house_id === this.selectedHouseId);
        console.log(`\nFiltered by house ${this.selectedHouseId}: ${filtered.length} tenants`);
        filtered.forEach(t => {
          console.log(`  - ${t.tenant_name} (Unit ${t.unit_number})`);
        });
      }
      
      // Filter by search
      const key = this.search.toLowerCase().trim();
      if (key) {
        filtered = filtered.filter(t =>
          (t.tenant_name?.toLowerCase() || '').includes(key) ||
          (t.tenant_phone?.toLowerCase() || '').includes(key) ||
          (t.tenant_idnumber?.toLowerCase() || '').includes(key)
        );
      }
      
      return filtered;
    },

    canSend() {
      return this.selectedTenants.length > 0 && 
             this.messageText.trim().length > 0 && 
             this.messageText.length <= 160 &&
             !this.sending;
    },

    fullMessageWithSignature() {
      if (!this.messageText) return "";
      const signature = this.businessSignature || "Regards";
      return `${this.messageText}\n\n${signature}`;
    }
  },

  methods: {
    async loadHouses() {
      try {
        const res = await apiFetch("/houses");
        console.log("\n=== HOUSES DATA ===");
        
        if (Array.isArray(res)) {
          this.houses = res;
        } else if (Array.isArray(res.data)) {
          this.houses = res.data;
        } else if (Array.isArray(res.houses)) {
          this.houses = res.houses;
        }
        
        console.log(`Loaded ${this.houses.length} houses`);
        this.houses.forEach(h => {
          console.log(`  - ID: ${h.id}, Name: ${h.name || h.house_name}`);
        });
      } catch (error) {
        console.error("Failed to load houses:", error);
        this.showToast("error", "Failed to load houses");
      }
    },

    async loadUnits() {
      try {
        const res = await apiFetch("/units");
        console.log("\n=== UNITS DATA ===");
        
        if (Array.isArray(res)) {
          this.allUnits = res;
        } else if (Array.isArray(res.data)) {
          this.allUnits = res.data;
        } else if (Array.isArray(res.units)) {
          this.allUnits = res.units;
        }
        
        console.log(`Loaded ${this.allUnits.length} units`);
        this.allUnits.forEach(unit => {
          console.log(`  - Unit ${unit.unit_number} (ID: ${unit.id}, House ID: ${unit.house_id}, Tenant ID: ${unit.tenant_id || 'none'})`);
        });
      } catch (error) {
        console.error("Failed to load units:", error);
        this.showToast("error", "Failed to load units");
      }
    },

    async loadTenants() {
      this.loading = true;
      try {
        const res = await apiFetch("/tenants");
        console.log("\n=== TENANTS DATA ===");
        
        if (Array.isArray(res)) {
          this.allTenants = res;
        } else if (Array.isArray(res.data)) {
          this.allTenants = res.data;
        } else if (Array.isArray(res.tenants)) {
          this.allTenants = res.tenants;
        } else {
          this.allTenants = [];
          console.warn("Unexpected tenants response shape", res);
        }
        
        console.log(`Loaded ${this.allTenants.length} tenants`);
        this.allTenants.forEach(tenant => {
          console.log(`  - ID: ${tenant.id}, Name: ${tenant.tenant_name}`);
        });
      } catch (e) {
        console.error("Failed to load tenants", e);
        this.showToast("error", "Failed to load tenants");
      } finally {
        this.loading = false;
      }
    },

    async loadBusinessProfile() {
      try {
        const res = await apiFetch("/profile");
        const profile = res.data || res;
        
        if (profile && profile.Business_name) {
          this.businessName = profile.Business_name;
          this.businessSignature = `Regards,\n${profile.Business_name}`;
        } else {
          this.businessSignature = "Regards";
        }
        
        console.log("\nBusiness profile loaded:", this.businessName);
      } catch (error) {
        console.error("Failed to load business profile:", error);
        this.businessSignature = "Regards";
      }
    },

    getUnitCountForHouse(houseId) {
      const unitsInHouse = this.allUnits.filter(u => u.house_id === houseId);
      return unitsInHouse.length;
    },

    getTenantCountForHouse(houseId) {
      // Find all units in this house that have tenant_id
      const unitsInHouse = this.allUnits.filter(u => u.house_id === houseId);
      const tenantsCount = unitsInHouse.filter(u => u.tenant_id).length;
      
      console.log(`\nHouse ${houseId} tenant count: ${tenantsCount}`);
      return tenantsCount;
    },

    selectByHouse(houseId) {
      this.selectedHouseId = houseId;
      this.selectedFilter = "house";
      
      console.log(`\n=== SELECTING HOUSE ID: ${houseId} ===`);
      
      // Find the house name
      const house = this.houses.find(h => h.id === houseId);
      console.log(`House: ${house?.name || house?.house_name}`);
      
      // Find all units in this house
      const unitsInHouse = this.allUnits.filter(u => u.house_id === houseId);
      console.log(`\nUnits in this house (${unitsInHouse.length} units):`);
      unitsInHouse.forEach(unit => {
        console.log(`  Unit ${unit.unit_number} (ID: ${unit.id}, Tenant ID: ${unit.tenant_id || 'none'})`);
      });
      
      // Get tenant IDs from units that have tenant_id
      const tenantIds = unitsInHouse
        .filter(unit => unit.tenant_id)
        .map(unit => unit.tenant_id);
      
      console.log(`\nTenant IDs found:`, tenantIds);
      
      // Update selected tenants
      this.selectedTenants = tenantIds;
      
      // Show detailed summary
      const houseName = house?.name || house?.house_name || `House ${houseId}`;
      const unitsCount = unitsInHouse.length;
      const tenantsCount = tenantIds.length;
      
      this.showToast("success", 
        `${houseName}: ${unitsCount} units, ${tenantsCount} tenants selected`
      );
      
      // Force UI update
      this.$forceUpdate();
    },

    selectAllTenants() {
      this.selectedFilter = "all";
      this.selectedHouseId = null;
      this.showHouseSelector = false;
      this.selectedTenants = this.filteredTenants.map(t => t.id);
      console.log("Selected all tenants:", this.selectedTenants.length);
    },

    clearHouseSelection() {
      this.selectedHouseId = null;
      this.selectedFilter = "all";
      this.selectedTenants = [];
    },

    clearSelection() {
      this.selectedTenants = [];
      this.selectedHouseId = null;
      this.selectedFilter = "all";
    },

    applyTemplate(type) {
      const templates = {
        rent: `Dear Tenant, this is a reminder that your rent payment is due. Please ensure you pay on time to avoid late fees. Thank you for your cooperation.`,
        maintenance: `Dear Tenant, maintenance work will be carried out on your unit. Please ensure access is available. Thank you for understanding.`,
        general: `Dear Tenant, this is a general notice from your landlord. For any inquiries, please contact the property manager. Thank you.`
      };
      this.messageText = templates[type] || "";
    },

    async sendSMS() {
      if (!this.canSend) return;

      this.sending = true;
      this.showToast("", "");

      const selectedTenantDetails = this.tenantsFromUnits.filter(t => 
        this.selectedTenants.includes(t.id)
      );

      console.log("\nSelected tenant details:", selectedTenantDetails);

      const tenantsWithoutPhone = selectedTenantDetails.filter(t => !t.tenant_phone);
      if (tenantsWithoutPhone.length > 0) {
        this.showToast(
          "error",
          `${tenantsWithoutPhone.length} tenant(s) have no phone number. Please update their contact info first.`
        );
        this.sending = false;
        return;
      }

      const smsData = {
        recipients: selectedTenantDetails.map(t => ({
          id: t.id,
          phone: t.tenant_phone,
          name: t.tenant_name
        })),
        message: this.fullMessageWithSignature,
        total_recipients: selectedTenantDetails.length
      };

      console.log("Sending SMS Data:", smsData);

      try {
        const res = await apiFetch("/tenant-sms", {
          method: "POST",
          body: JSON.stringify(smsData)
        });

        this.showToast(
          "success",
          res.message || `Successfully sent SMS to ${selectedTenantDetails.length} tenant(s)!`
        );
        
        // Clear form after successful send
        this.selectedTenants = [];
        this.messageText = "";
        this.selectedHouseId = null;
        this.selectedFilter = "all";
        this.showHouseSelector = false;
        
      } catch (err) {
        console.error("Failed to send SMS:", err);
        this.showToast(
          "error",
          err.message || "Failed to send SMS. Please try again."
        );
      } finally {
        this.sending = false;
      }
    },

    showToast(type, message) {
      this.toastType = type === "success" ? "toast-success" : "toast-error";
      this.toastMessage = message;
      setTimeout(() => {
        if (this.toastMessage === message) {
          this.toastMessage = "";
        }
      }, 5000);
    }
  },

  async mounted() {
    console.log("========================================");
    console.log("=== STARTING DATA LOAD ===");
    console.log("========================================\n");
    
    await this.loadHouses();    // Load all houses
    await this.loadUnits();     // Load all units  
    await this.loadTenants();   // Load all tenants
    await this.loadBusinessProfile();
    
    console.log("\n========================================");
    console.log("=== DATA LOAD COMPLETE ===");
    console.log("========================================");
    
    // Special debug for house_id 2
    console.log("\n=== DEBUG: House ID 2 ===");
    const house2 = this.houses.find(h => h.id === 2);
    if (house2) {
      console.log(`Found house: ${house2.name || house2.house_name}`);
      const unitsInHouse2 = this.allUnits.filter(u => u.house_id === 2);
      console.log(`Units in house 2: ${unitsInHouse2.length}`);
      const tenantIdsFromUnits = unitsInHouse2.map(u => u.tenant_id).filter(id => id);
      console.log(`Tenant IDs from units:`, tenantIdsFromUnits);
      
      // Get tenant details
      const tenantsList = this.allTenants.filter(t => tenantIdsFromUnits.includes(t.id));
      console.log(`Tenant details:`, tenantsList.map(t => ({ id: t.id, name: t.tenant_name, phone: t.tenant_phone })));
    } else {
      console.log("House ID 2 not found!");
    }
  }
};
</script>
<style scoped>
/* House Selection Styles */
.house-selection {
  padding: 0 4px;
}

.house-selector {
  margin-top: 10px;
  padding: 10px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-sm);
  border: 1px solid var(--dash-line);
}

.house-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.house-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid var(--dash-line-strong);
  border-radius: var(--dash-radius-lg);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.house-btn:hover {
  background: var(--dash-line);
  border-color: var(--dash-primary);
}

.house-btn.active {
  background: var(--dash-primary);
  border-color: var(--dash-primary);
  color: white;
}

.house-btn.active .badge {
  background: white !important;
  color: var(--dash-primary);
}

.selection-summary-bar {
  padding: 8px 12px;
  background: var(--dash-bg-alt);
  border-radius: var(--dash-radius-sm);
}

/* Rest of your existing styles */
.sms-container {
  padding: 30px 40px;
  background: var(--dash-bg);
  min-height: calc(100vh - 80px);
}

.page-title {
  font-weight: 700;
  color: var(--dash-primary);
  margin: 0;
}

/* Cards */
.selection-card, .message-card {
  border-radius: var(--dash-radius-lg);
  background: white;
  min-height: 500px;
}

.card-header {
  border-bottom: 1px solid var(--dash-line);
}

/* Search */
.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-faint);
  z-index: 10;
}

.search-box {
  width: 100%;
  padding-left: 40px;
  border-radius: var(--dash-pill);
  border: 1px solid var(--dash-line);
  background: white;
  transition: all 0.2s;
}

.search-box:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  border-color: var(--dash-primary);
}

/* Tenant List */
.tenant-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 5px;
}

.tenant-item {
  padding: 12px;
  border-radius: var(--dash-radius-sm);
  margin-bottom: 8px;
  transition: all 0.2s;
  cursor: pointer;
}

.tenant-item:hover {
  background: var(--dash-surface-2);
}

.tenant-item.selected {
  background: var(--dash-primary-soft);
  border-left: 3px solid var(--dash-primary);
}

.tenant-info {
  margin-left: 8px;
}

.tenant-name {
  font-weight: 600;
  color: var(--dash-ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tenant-name .badge {
  font-size: 10px;
  padding: 3px 8px;
}

.tenant-details {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tenant-details .badge {
  font-weight: 400;
  font-size: 11px;
  padding: 4px 8px;
}

/* Message Input */
.message-input-wrapper {
  position: relative;
}

.message-input {
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
  font-size: 14px;
  resize: none;
}

.message-input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  border-color: var(--dash-primary);
}

.character-counter {
  text-align: right;
  font-size: 12px;
  color: var(--dash-faint);
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 5px;
}

/* Business Info */
.business-info {
  font-size: 12px;
  padding: 6px 12px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-xs);
}

/* Message Preview */
.message-preview {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  padding: 12px;
  border-left: 3px solid var(--dash-primary);
}

.preview-header {
  font-size: 12px;
  font-weight: 600;
  color: var(--dash-muted);
  margin-bottom: 8px;
}

.preview-content {
  font-size: 14px;
  color: var(--dash-ink);
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.character-counter-warning {
  margin-top: 8px;
}

/* Quick Templates */
.template-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.template-buttons .btn {
  font-size: 12px;
  padding: 4px 12px;
}

/* Send Button */
.btn-primary {
  background: var(--dash-primary);
  border: none;
  padding: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Toast Message */
.toast-message {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: var(--dash-radius-sm);
  background: white;
  box-shadow: var(--dash-shadow);
  z-index: 1000;
  min-width: 300px;
  animation: slideInRight 0.3s ease-out;
}

.toast-success {
  border-left: 4px solid var(--dash-success);
  color: var(--dash-success-ink);
}

.toast-error {
  border-left: 4px solid var(--dash-danger);
  color: var(--dash-danger-ink);
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .sms-container {
    padding: 20px;
  }

  .toast-message {
    left: 20px;
    right: 20px;
    min-width: auto;
  }
  
  .house-list {
    max-height: 150px;
  }
}
</style>